import React from "react";
import { Box, Typography, IconButton, Tooltip, Link } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useWatchListContext } from "../contexts/WatchListContext";
import { formatPerformance } from "../pages/Leaderboard";
import { showToast } from "../utils/toast";
import TableView from "./common/TableView";
import { ColumnConfig } from "./common/TableView";
import StockLink from "./common/StockLink";
import WatchListCard from "./WatchListCard";

const WatchListTable: React.FC = () => {
  const { watchList, isLoading, removeFromWatchList } = useWatchListContext();

  const handleRemoveFromWatchList = async (id: number) => {
    try {
      await removeFromWatchList(id);
      showToast.success("Removed from watch list! 👋");
    } catch (error) {
      showToast.error("Failed to remove from watch list");
    }
  };

  const columns: ColumnConfig[] = [
    {
      label: "Symbol",
      key: "symbol",
      render: (_, row) => (
        <Box>{row.symbol ? <StockLink symbol={row.symbol} /> : "-"}</Box>
      ),
    },
    {
      label: "Name",
      key: "name",
      minWidth: 300,
      render: (_, row) => (
        <Tooltip title={row.name || "-"}>
          <Box
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "300px",
              width: "100%",
              display: "block",
            }}
          >
            {row.name || "-"}
          </Box>
        </Tooltip>
      ),
    },
    {
      label: "Latest Price",
      key: "latestPrice",
      render: (_, row) => (row.latestPrice ? `Rs. ${row.latestPrice}` : "-"),
    },
    {
      label: "1 Day",
      key: "performance1D",
      render: (_, row) =>
        row.performance1D ? formatPerformance(row.performance1D) : "-",
    },
    {
      label: "1 Week",
      key: "performance1W",
      render: (_, row) =>
        row.performance1W ? formatPerformance(row.performance1W) : "-",
    },
    {
      label: "1 Month",
      key: "performance1M",
      render: (_, row) =>
        row.performance1M ? formatPerformance(row.performance1M) : "-",
    },
    {
      label: "Actions",
      key: "actions",
      render: (_, row) => (
        <Tooltip title="Remove from watch list">
          <IconButton
            size="small"
            onClick={() => handleRemoveFromWatchList(row.id)}
            sx={{
              color: "error.main",
              "&:hover": { backgroundColor: "error.lighter" },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 2, paddingLeft: { xs: 2, sm: 0 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <VisibilityIcon fontSize="small" aria-hidden />
          <Typography variant="body1" component="h2" sx={{ fontWeight: "bold" }}>
            Your Watch List
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Add stocks to your watch list from the{" "}
          <Link href="#/stocks" color="primary">
            Stocks
          </Link>{" "}
          page
        </Typography>
      </Box>

      {isLoading ? (
        <Typography>Loading watch list...</Typography>
      ) : !watchList?.length ? (
        <Typography variant="body2" color="text.secondary">
          Your watch list is empty. Add stocks to track them here!
        </Typography>
      ) : (
        <TableView
          columns={columns}
          tableData={watchList}
          isCompact
          customCardComponent={(row) => (
            <WatchListCard row={row} onRemove={handleRemoveFromWatchList} />
          )}
        />
      )}
    </Box>
  );
};

export default WatchListTable;
